<script lang="ts">
					import { defineComponent } from 'vue'
					export default defineComponent({
						name: "undefined"
					})
				</script><template>
	<el-dialog
		v-model="visible"
		:title="copy.title"
		width="520px"
		append-to-body
		:z-index="10050"
		:close-on-click-modal="false"
		:close-on-press-escape="false"
		:before-close="handleBeforeClose"
		destroy-on-close
		@closed="handleClosed"
	>
		<div class="oss-archive-restore-dialog">
			<p class="dialog-body">{{ copy.body }}</p>
			<p v-if="displayPid" class="dialog-pid">PID: {{ displayPid }}</p>
			<p v-if="metaText" class="dialog-meta">{{ metaText }}</p>
			<button type="button" class="why-link" @click="showHelp = true">
				<i>?</i>
				{{ whyLinkText }}
			</button>
		</div>

		<template #footer>
			<div class="dialog-footer">
				<el-button v-if="copy.secondary" @click="handleSecondary">{{ copy.secondary }}</el-button>
				<el-button
					v-if="copy.primary"
					type="primary"
					:loading="loading"
					:disabled="loading"
					@click="handlePrimary"
				>
					{{ copy.primary }}
				</el-button>
			</div>
		</template>
	</el-dialog>

	<el-dialog v-model="showHelp" :title="helpTitle" width="520px" append-to-body :z-index="10100">
		<div class="help-body">{{ helpBody }}</div>
		<template #footer>
			<el-button @click="showHelp = false">{{ helpCloseText }}</el-button>
		</template>
	</el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { service } from "/@/cool/service";
import {
	getOssArchiveDialogCopy,
	getOssArchiveHelpWhyBody,
	getOssArchiveHelpWhyTitle,
	getOssArchiveToastCopy,
	getOssArchiveWhyLinkText,
	getOssArchiveCloseText,
	resolveDialogStateFromRestoreStatus,
	resolveOssArchiveLocale,
	type OssArchiveDialogState,
	type OssArchiveUiCopy
} from "/$/common/utils/oss-archive-dialog";
import {
	mapErrorCodeToDialogState,
	type OssArchiveRejectError
} from "/$/common/utils/oss-archive-error";
import {
	closeOssArchiveRestoreDialog,
	ossArchivePreviewBlocked,
	ossArchiveRestoreDialogPayload,
	ossArchiveRestoreDialogVisible
} from "/$/common/utils/oss-archive-handler";

const props = defineProps<{
	modelValue?: boolean;
	error?: OssArchiveRejectError;
	pid?: number;
	orderId?: number;
	objectKeys?: string[];
	restoreStatus?: string;
	uiCopy?: OssArchiveUiCopy;
	locale?: string;
	/** 调试台：按 object key 解冻（可不依赖数字 PID） */
	applyByObjectKey?: boolean;
	debugBucket?: string;
	debugCredentials?: {
		access_key_id?: string;
		access_key_secret?: string;
		endpoint?: string;
	};
}>();

const emit = defineEmits<{
	(e: "update:modelValue", value: boolean): void;
	(e: "ready"): void;
	(e: "closed"): void;
}>();

const loading = ref(false);
const showHelp = ref(false);
const localRestoreStatus = ref<string>("none");
const localPid = ref<number | undefined>();

const visible = computed({
	get() {
		// Host 不传 v-model 时 Boolean 会把 modelValue 收成 false，?? 会把全局 visible 盖掉
		return ossArchiveRestoreDialogVisible.value || Boolean(props.modelValue);
	},
	set(value: boolean) {
		emit("update:modelValue", value);
		ossArchiveRestoreDialogVisible.value = value;
		if (!value) {
			// 点关闭立刻去掉预览遮罩，不等 el-dialog @closed
			ossArchivePreviewBlocked.value = false;
		}
		// 不在此处清 payload：el-dialog 关闭动画期间仍按原内容渲染，避免出现授权态闪屏；
		// payload 统一在 closed（动画结束）后由 handleClosed 清理。
	}
});

const activePayload = computed(() => {
	if (props.error || props.pid || props.objectKeys?.length) {
		return {
			error: props.error,
			pid: props.pid,
			orderId: props.orderId,
			objectKeys: props.objectKeys,
			restoreStatus: props.restoreStatus,
			uiCopy: props.uiCopy
		};
	}
	return ossArchiveRestoreDialogPayload.value || {};
});

const uiCopy = computed<OssArchiveUiCopy | undefined>(() => activePayload.value.uiCopy);
const activeLocale = computed(() => props.locale || resolveOssArchiveLocale());

const dialogState = computed<OssArchiveDialogState>(() => {
	const errorCode = activePayload.value.error?.error_code || activePayload.value.error?.data?.error_code;
	if (errorCode) {
		return mapErrorCodeToDialogState(String(errorCode), localRestoreStatus.value);
	}
	return resolveDialogStateFromRestoreStatus(localRestoreStatus.value);
});

const copy = computed(() => {
	const raw = getOssArchiveDialogCopy(dialogState.value, uiCopy.value, activeLocale.value);
	if (dialogState.value !== "pending") {
		return raw;
	}
	return {
		...raw,
		primary: "",
		secondary: raw.secondary || getOssArchiveCloseText(uiCopy.value, activeLocale.value)
	};
});
const helpBody = computed(() => getOssArchiveHelpWhyBody(uiCopy.value, activeLocale.value));
const helpTitle = computed(() => getOssArchiveHelpWhyTitle(uiCopy.value, activeLocale.value));
const whyLinkText = computed(() => getOssArchiveWhyLinkText(uiCopy.value, activeLocale.value));
const helpCloseText = computed(() => getOssArchiveCloseText(uiCopy.value, activeLocale.value));
const toasts = computed(() => getOssArchiveToastCopy(uiCopy.value, activeLocale.value));

function resolvePid(): number | undefined {
	const pid = localPid.value || activePayload.value.pid || activePayload.value.error?.data?.pid;
	return pid && Number(pid) > 0 ? Number(pid) : undefined;
}

const displayPid = computed(() => resolvePid());

function resolveObjectKeys(): string[] {
	const raw =
		activePayload.value.objectKeys || activePayload.value.error?.data?.object_keys || [];
	const seen = new Set<string>();
	const out: string[] = [];
	for (const item of raw) {
		const key = String(item || "")
			.trim()
			.replace(/^\/+/, "");
		if (!key || key.endsWith("/") || seen.has(key)) continue;
		seen.add(key);
		out.push(key);
	}
	return out;
}

const metaText = computed(() => {
	if (!props.applyByObjectKey) {
		return "";
	}
	const keys = resolveObjectKeys();
	if (keys.length === 1) {
		return `对象: ${keys[0]}`;
	}
	if (keys.length > 1) {
		return `对象数: ${keys.length} · 首个: ${keys[0]}`;
	}
	return "";
});

watch(
	() => activePayload.value,
	(payload) => {
		localPid.value = payload.pid || payload.error?.data?.pid;
		localRestoreStatus.value =
			payload.restoreStatus || payload.error?.data?.restore_status || "none";
	},
	{ immediate: true, deep: true }
);

async function handlePrimary() {
	switch (dialogState.value) {
		case "need":
		case "expired":
		case "failed":
			await submitRestoreApply();
			break;
		case "ready":
			emit("ready");
			visible.value = false;
			break;
		default:
			visible.value = false;
	}
}

function handleSecondary() {
	visible.value = false;
}

async function submitRestoreApply() {
	const pid = resolvePid();
	const keys = resolveObjectKeys();
	if (!pid && !keys.length) {
		ElMessage.error(toasts.value.missingPid);
		return;
	}
	loading.value = true;
	try {
		let res: any;
		if (props.applyByObjectKey && keys.length) {
			res = await service.ossArchive.debugForceRestoreApply({
				key: keys[0],
				object_key: keys[0],
				bucket: props.debugBucket,
				...(props.debugCredentials || {})
			});
		} else {
			res = await service.ossArchive.restoreApply({
				...(pid ? { pid } : {}),
				...(!pid && keys.length === 1 ? { object_key: keys[0] } : {}),
				...(!pid && keys.length > 1 ? { object_keys: keys } : {})
			});
		}
		if (res?.pid && Number(res.pid) > 0) {
			localPid.value = Number(res.pid);
		}
		if (res?.merged) {
			ElMessage.info(toasts.value.applyMerged);
		} else {
			ElMessage.success(toasts.value.applySuccess);
		}
		const status = String(res?.restore_status || "pending");
		// 极少数已可读：直接继续；否则进入办理中提示，由客户稍后自行再点预览
		if (status === "ready") {
			localRestoreStatus.value = "ready";
			emit("ready");
			visible.value = false;
			return;
		}
		localRestoreStatus.value = status === "failed" ? "failed" : "pending";
	} catch (err: any) {
		ElMessage.error(err?.message || toasts.value.applyFailed);
	} finally {
		loading.value = false;
	}
}

function handleBeforeClose(done: () => void) {
	// 右上角 ×：el-dialog 默认到 afterLeave 才 emit update:modelValue，
	// 期间全局 visible 滞留 true，会把关闭动画内的再次打开吞掉；这里立即同步关闭。
	visible.value = false;
	done();
}

function handleClosed() {
	showHelp.value = false;
	closeOssArchiveRestoreDialog();
	emit("closed");
}
</script>

<style scoped lang="scss">
.oss-archive-restore-dialog {
	.dialog-body {
		margin: 0 0 12px;
		line-height: 1.7;
		color: var(--el-text-color-primary);
		white-space: pre-wrap;
	}

	.dialog-pid {
		margin: 0 0 12px;
		font-size: 14px;
		line-height: 1.7;
		color: var(--el-text-color-primary);
	}

	.dialog-meta {
		margin: 0 0 12px;
		font-size: 12px;
		color: var(--el-text-color-secondary);
	}

	.why-link {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 0;
		border: 0;
		background: none;
		color: var(--el-color-primary);
		font: inherit;
		font-size: 13px;
		cursor: pointer;
		text-decoration: underline;

		i {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 14px;
			height: 14px;
			border: 1px solid currentColor;
			border-radius: 50%;
			font-style: normal;
			font-size: 10px;
		}
	}
}

.help-body {
	line-height: 1.7;
	color: var(--el-text-color-primary);
	white-space: pre-wrap;
}

.dialog-footer {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
}
</style>
